import React, { useMemo } from 'react'

import Box from 'components_new/atoms/Box'

import DataRunsFailList from 'components_new/organisms/internal/DataRuns/FailList'
import DataRunsFileDeliveries from 'components_new/organisms/internal/DataRuns/FileDeliveries'
import DataRunsKpiRow from 'components_new/organisms/internal/DataRuns/KpiRow'
import DataRunsOrderWarnings from 'components_new/organisms/internal/DataRuns/OrderWarnings'
import DataRunsRunningNow from 'components_new/organisms/internal/DataRuns/RunningNow'
import DataRunsStatusGrid from 'components_new/organisms/internal/DataRuns/StatusGrid'
import DataRunsTimeline from 'components_new/organisms/internal/DataRuns/Timeline'
import DataRunsWorkflows from 'components_new/organisms/internal/DataRuns/Workflows'

import { DataRunsCustomerOption } from 'types/GlobalDataRuns'
import {
  DataRunsData,
  FileDelivery,
  JobSummary
} from 'redux/selectors/internal/DataRuns'
import {
  jobKey,
  jobLookupKey
} from 'components_new/organisms/internal/DataRuns/utils'

/**
 * The Jobb tab: the page that used to live at /internal/data-runs,
 * body unchanged. What it lost to the shared Datahälsa header is its own
 * title, its own customer select and its own run-context line - the customer
 * scope now arrives as a prop, and so does the data fetched for it.
 */
export interface DataRunsTabProps {
  customerOptions: DataRunsCustomerOption[]
  data: DataRunsData | null
  /** null = all customers. Owned by the Datahälsa route. */
  selectedCustomerId: string | null
  /**
   * Exact signature HP-307/HP-308 build on: called with the full run for
   * any clicked job row / chip / warning that resolves to one. A no-op
   * default keeps this page fully wired ahead of the detail panel landing.
   */
  onSelectJobRun: (job: JobSummary) => void
  /** Mirrors `onSelectJobRun` for the file-delivery rows. */
  onSelectFileDelivery: (delivery: FileDelivery) => void
}

const DataRunsTab = (props: DataRunsTabProps) => {
  const {
    customerOptions,
    data,
    onSelectFileDelivery,
    onSelectJobRun,
    selectedCustomerId
  } = props

  const selectedCustomer = selectedCustomerId
    ? customerOptions.find((customer) => customer.id === selectedCustomerId) ??
      null
    : null

  const failingJobs = useMemo(
    () =>
      (data?.jobs ?? []).filter(
        (job) => job.status === 'fail' || job.status === 'timeout'
      ),
    [data]
  )

  const jobByKey = useMemo(() => {
    const jobs = data?.jobs ?? []
    const map = new Map<string, JobSummary>()

    jobs.forEach((job) => map.set(jobKey(job), job))

    return map
  }, [data])

  /**
   * The customer has to come along: a job name is only unique together with
   * a customer (the global singleton jobs run once per customer under one
   * name), so a name-only lookup can open another customer's run.
   */
  const handleSelectJobByName = (jobName: string, customerId?: string) => {
    const job = jobByKey.get(jobLookupKey(jobName, customerId))

    if (job) {
      onSelectJobRun(job)
    }
    // No JobSummary exists yet for this name (e.g. a workflow-graph job
    // that has never produced a run) - nothing to open until HP-308 adds
    // a name-only fallback view.
  }

  const scopeKey = selectedCustomerId ?? 'all'

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '18px'
      }}
    >
      {data ? (
        <>
          <DataRunsKpiRow
            aggregates={data.aggregates}
            customerName={selectedCustomer?.name ?? null}
            okJobCount={data.aggregates.job_count - data.aggregates.fail_count}
            orderWarningCount={data.order_warnings.length}
            totalCustomers={customerOptions.length}
          />

          <DataRunsRunningNow
            onSelectJobByName={handleSelectJobByName}
            running={data.running}
          />

          <DataRunsFailList
            jobs={failingJobs}
            key={`fail-${scopeKey}`}
            onSelectJobRun={onSelectJobRun}
          />

          {/*
            Above the ordering heuristics because a missing customer file
            *explains* the job failures below it, but under the hard
            failures because a failed Glue job is a fact while a late file
            is derived from observed cadence.
          */}
          <DataRunsFileDeliveries
            fileDeliveries={data.file_deliveries}
            isCustomerScope={!!selectedCustomer}
            key={`deliveries-${scopeKey}`}
            onSelectFileDelivery={onSelectFileDelivery}
          />

          <DataRunsOrderWarnings
            onSelectJobByName={handleSelectJobByName}
            orderWarnings={data.order_warnings}
          />

          {selectedCustomer ? (
            <DataRunsTimeline
              fileDeliveries={data.file_deliveries}
              jobs={data.jobs}
              onSelectFileDelivery={onSelectFileDelivery}
              onSelectJobRun={onSelectJobRun}
            />
          ) : null}

          {selectedCustomer ? (
            <DataRunsStatusGrid
              fileDeliveries={data.file_deliveries}
              jobs={data.jobs}
              onSelectFileDelivery={onSelectFileDelivery}
              onSelectJobRun={onSelectJobRun}
            />
          ) : null}

          {/*
            Least important section on the page, so it sits last - a
            workflow is an extra run on top of the nightly batch, and the
            nightly batch is what the sections above are about.
          */}
          <DataRunsWorkflows
            jobs={data.jobs}
            onSelectJobByName={handleSelectJobByName}
            onSelectJobRun={onSelectJobRun}
            workflows={data.workflows}
          />
        </>
      ) : null}
    </Box>
  )
}

DataRunsTab.displayName = 'DataRunsTab'
export default DataRunsTab
